import { useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';

import type {
  CreateAdminUserFilterRequest,
  GetAdminUserFilterByIdRequest,
  GetAdminUserFilterByIdResponse,
} from '@coursera/grpc-types-entsavedfilters/coursera/proto/entsavedfilters/v1beta1/enterprise_saved_filters_api';

import type { SavedAdminFilter, SavedAdminFilterFromServer, UserFilter } from './types';
import { mapUserFilterFromClientToServerModel, mapUserFilterFromServerToClientModel } from './utils';

export const createAdminUserFilterMutation = gql`
  mutation CreateAdminUserFilterMutation($input: CreateAdminUserFilterRequest!) {
    CreateAdminUserFilterMutation(input: $input)
      @rest(
        type: "CreateAdminUserFilter"
        path: "grpc/entsavedfilters/v1beta1/EnterpriseSavedFiltersAPI/CreateAdminUserFilter"
        method: "POST"
      ) {
      adminUserFilter
    }
  }
`;

export type CreateAdminUserFilterMutation = {
  createAdminUserFilter: (request: {
    variables: {
      input: CreateAdminUserFilterRequest;
    };
  }) => Promise<{
    data?: {
      CreateAdminUserFilterMutation: {
        adminUserFilter: { filterId: string };
      };
    } | null;
  }>;
};

export const useCreateAdminUserFilterMutation = () => {
  const [createAdminUserFilter] = useMutation<
    {
      CreateAdminUserFilterMutation: {
        adminUserFilter: { filterId: string };
      };
    },
    {
      input: CreateAdminUserFilterRequest;
    }
  >(createAdminUserFilterMutation);

  return createAdminUserFilter;
};

export const useCreateAdminUserFilter = (org: { id: string }) => {
  const createAdminUserFilter = useCreateAdminUserFilterMutation();
  return async (searchNameOrEmails: string, savedFilter?: SavedAdminFilter, filterPurpose = 'FILTER_PURPOSE_REUSE') => {
    const filter =
      savedFilter &&
      mapUserFilterFromClientToServerModel(savedFilter.filter, {
        searchNameOrEmails: searchNameOrEmails ? [searchNameOrEmails] : undefined,
      });

    const input = {
      organizationId: org.id,
      filterName: '',
      filterPurpose,
      filter,
    };
    const response = await createAdminUserFilter({ variables: { input } });
    return response.data?.CreateAdminUserFilterMutation.adminUserFilter.filterId;
  };
};

export const getAdminUserFilterByIdQuery = gql`
  query GetAdminUserFilterByIdQuery($input: GetAdminUserFilterByIdRequest!) {
    GetAdminUserFilterByIdQuery(input: $input)
      @rest(
        type: "GetAdminUserFilterById"
        path: "grpc/entsavedfilters/v1beta1/EnterpriseSavedFiltersAPI/GetAdminUserFilterById"
        method: "POST"
      ) {
      adminUserFilter
    }
  }
`;

type AdminUserFilterByIdResult = {
  /** Client-mapped filter; undefined until loaded, or when there's no `filterId` to fetch. */
  clientFilter: UserFilter | undefined;
  loading: boolean;
};

/**
 * Fetches a previously saved AdminUserFilter's params by id
 * (`EnterpriseSavedFiltersAPI/GetAdminUserFilterById`), mapped to the client
 * `UserFilter` model — the read counterpart to `useCreateAdminUserFilterMutation`.
 * Skips the request while `filterId` is empty/undefined. Takes `userId`
 * as an argument (rather than reading `js/lib/user` itself) so this hook
 * stays a pure data-fetching hook, with identity resolution left to the caller.
 */
export const useAdminUserFilterById = (filterId: string | undefined, userId: number): AdminUserFilterByIdResult => {
  const { data, loading } = useQuery<
    { GetAdminUserFilterByIdQuery: GetAdminUserFilterByIdResponse },
    { input: GetAdminUserFilterByIdRequest }
  >(getAdminUserFilterByIdQuery, {
    variables: { input: { filterId: filterId ?? '', userId } },
    skip: !filterId,
    errorPolicy: 'all',
    // Callers use this to snapshot the CURRENT filter params (e.g. to mint a new
    // filter from them on resend) — an Apollo cache hit could serve stale params
    // if the same filterId was fetched earlier in the session and has since changed.
    fetchPolicy: 'network-only',
  });

  const filter = data?.GetAdminUserFilterByIdQuery?.adminUserFilter as SavedAdminFilterFromServer | undefined;

  return {
    clientFilter: filter ? mapUserFilterFromServerToClientModel(filter) : undefined,
    // Apollo reports `loading: true` for skipped queries too; only reflect it when there's actually a filterId to fetch.
    loading: Boolean(filterId) && loading,
  };
};
